import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { TextFieldProps } from '@mui/material';
import { AppInput } from 'components/app-input';
import { useTranslate } from 'hooks/use-translate';
import {
  FILTERS_SHEET_PLACEHOLDER_KEY,
  useIsInFiltersSheet,
} from 'components/filters-dialog/context';
import React, { forwardRef, memo, useCallback, useMemo } from 'react';
import { SelectListBox, SelectPaper, SelectPopper } from './components';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material';
import { AutocompleteValue, FilterOptionsState } from '@mui/material/useAutocomplete';

import { useOpen } from 'AurionCR/components/hooks';
import clsx from 'clsx';
import { objectSlotProps } from 'utils/slot-props';

export const defaultGetOptionLabel = <T extends { title?: string }>(option: T) => {
  return option['title'] || '';
};

export const defaultGetOptionValue = <T extends { id?: string }>(option: T) => {
  return option['id'] || '';
};

const defOptions: any[] = [];

export const defaultGetOptionDisabled = <T extends Record<string, any> = { isActive?: boolean }>(
  option: T,
) => {
  return option?.isActive === false;
};

export interface AppSelectProps<
  T extends Record<string, any> = { id: string; title: string; isActive?: boolean },
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends Omit<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    'value' | 'options' | 'onChange' | 'renderInput' | 'renderOption' | 'getOptionLabel'
  > {
  getOptionLabel?: (option: T) => string;
  /**
   * Option renderer with the v4 signature `(option, state) => node`. MUI 5's
   * `renderOption(props, option, state)` expects the caller to render the
   * `<li {...props}>` itself; AppSelect does that once for all its consumers.
   */
  renderOption?: (option: T, state: AutocompleteRenderOptionState) => React.ReactNode;
  getOptionValue?: (option: T) => string;
  placeholder?: string;
  readOnly?: boolean;
  label?: React.ReactNode;
  options?: T[];
  value?: Multiple extends true ? any[] : any;
  onChange?: (v: Multiple extends true ? any[] : any, option: T) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  /** Props of the AppInput the select renders (MUI 9 shape: `slotProps.input` / `.inputLabel` / `.htmlInput`). */
  RenderInputProps?: Partial<Omit<TextFieldProps, 'value' | 'onChange'>>;
}

const AppSelectComponent = <
  T extends Record<string, any> = { id: string; title: string; isActive: boolean },
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  {
    onChange,
    value,
    options = defOptions,
    error,
    helperText,
    loading = false,
    label,
    required,
    placeholder,
    RenderInputProps,
    getOptionDisabled = defaultGetOptionDisabled,
    limitTags = -1,
    readOnly,
    getOptionLabel = defaultGetOptionLabel,
    getOptionValue = defaultGetOptionValue,
    ...rest
  }: AppSelectProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: any,
) => {
  const { t } = useTranslate();
  const { isOpen, handleOpen, handleClose } = useOpen({ init: false });
  // N2 (T5, DC-18 a): in a list's filters sheet (below md) an empty select
  // reads "all" under its raised label
  const isInFiltersSheet = useIsInFiltersSheet();
  const fieldPlaceholder =
    placeholder || (isInFiltersSheet ? t(FILTERS_SHEET_PLACEHOLDER_KEY) : placeholder);
  const InputProps = useMemo(() => {
    const { slotProps, ...renderInputProps } = RenderInputProps ?? {};
    return {
      required,
      label,
      placeholder: fieldPlaceholder,
      error,
      helperText,
      disableClearable: true,
      ...renderInputProps,
      inputRef: ref,
      slotProps: {
        ...slotProps,
        input: objectSlotProps(slotProps?.input),
        inputLabel: objectSlotProps(slotProps?.inputLabel),
        htmlInput: {
          autoComplete: 'nope',
          ...objectSlotProps(slotProps?.htmlInput),
        },
      },
    };
  }, [required, label, fieldPlaceholder, error, helperText, RenderInputProps, ref]);

  const _renderInput = useCallback(
    (p: AutocompleteRenderInputParams) => {
      // MUI 9: the Autocomplete hands its input wiring in `params.slotProps`.
      const { slotProps: paramsSlotProps, ...params } = p;
      return (
        <AppInput
          {...InputProps}
          {...params}
          slotProps={{
            ...InputProps.slotProps,
            inputLabel: {
              ...paramsSlotProps.inputLabel,
              ...InputProps.slotProps.inputLabel,
            },
            htmlInput: {
              ...InputProps.slotProps.htmlInput,
              ...paramsSlotProps.htmlInput,
            },
            input: {
              readOnly: readOnly,
              ...InputProps.slotProps.input,
              ...paramsSlotProps.input,
              endAdornment: (
                <>
                  {paramsSlotProps.input.endAdornment}
                  {InputProps.slotProps.input?.endAdornment}
                </>
              ),
            },
          }}
        />
      );
    },
    [InputProps, readOnly],
  );

  const renderOption = rest.renderOption;
  const _renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
      option: T,
      state: AutocompleteRenderOptionState,
    ) => {
      const { key, ...liProps } = props;
      return (
        <li key={key} {...liProps}>
          {renderOption ? renderOption(option, state) : getOptionLabel(option)}
        </li>
      );
    },
    // eslint-disable-next-line
    [getOptionLabel, renderOption],
  );

  const filterOptions = rest.filterOptions;
  const _filterOptions = useCallback(
    (options: Array<T>, state: FilterOptionsState<T>) => {
      const filtered = filterOptions
        ? filterOptions(options, state)
        : options.filter((item: T) =>
            state.getOptionLabel(item).toLowerCase().includes(state.inputValue.toLowerCase()),
          );
      return filtered;
    },
    // eslint-disable-next-line
    [filterOptions],
  );

  const handleChange = useCallback(
    (
      event: React.SyntheticEvent,
      option: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
    ) => {
      if (Array.isArray(option)) {
        const value = option
          .map((optionItem) => getOptionValue(optionItem as T))
          .filter((v) => !!v);
        onChange && onChange(value, option as any);
      } else {
        const value = (option && getOptionValue(option as T)) || '';
        onChange && onChange(value as any, option as any);
      }
    },
    // eslint-disable-next-line
    [onChange, getOptionValue],
  );

  const selectedValue = useMemo(() => {
    const ops = options || [];
    if (Array.isArray(value)) {
      const values = value || [];
      return ops.filter((option) => values.some((v: any) => getOptionValue(option) === v));
    } else if (value) {
      return ops.find((option) => getOptionValue(option) === value) || '';
    } else {
      return rest.multiple ? [] : '';
    }
  }, [value, getOptionValue, rest.multiple, options]) as AutocompleteValue<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >;

  const noOptionsText = t('no-options');
  return (
    <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
      value={selectedValue}
      noOptionsText={noOptionsText}
      getOptionDisabled={getOptionDisabled}
      open={isOpen}
      onOpen={readOnly ? undefined : handleOpen}
      onClose={handleClose}
      {...rest}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : getOptionLabel(option as T)
      }
      disableClearable={(readOnly ? true : rest.disableClearable) as any}
      className={clsx(
        rest.className,
        !(readOnly || rest.disableClearable || rest.disabled) && 'autocomplete-clearable',
      )}
      renderInput={rest.renderInput || _renderInput}
      renderOption={_renderOption}
      options={options}
      onChange={handleChange}
      loading={loading}
      popupIcon={
        readOnly ? null : (
          <ArrowDropDownRoundedIcon color={'inherit'} style={{ color: 'rgba(0,0,0,0.3)' }} />
        )
      }
      limitTags={limitTags}
      filterOptions={_filterOptions}
      slots={{ paper: SelectPaper, popper: SelectPopper, ...rest.slots }}
      slotProps={{
        ...rest.slotProps,
        listbox: { component: SelectListBox, ...rest.slotProps?.listbox },
      }}
    />
  );
};

// React 19 types: a JSX component takes one argument, so the generic signature is restated with the ref as a prop
export const AppSelect = memo(forwardRef(AppSelectComponent)) as <
  T extends Record<string, any> = { id: string; title: string; isActive: boolean },
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: AppSelectProps<T, Multiple, DisableClearable, FreeSolo> & React.RefAttributes<any>,
) => React.ReactElement;
